// Application entry point for Vite
// This loads Stimulus controllers and core JavaScript

// Import styles
import './application.css'

// Core Rails JavaScript
import '@hotwired/turbo-rails'
import Rails from '@rails/ujs'
Rails.start()
import * as ActiveStorage from '@rails/activestorage'
ActiveStorage.start()
import '../src/confirm'

// Import dashboard new event modal (only loads when dashboard page is present)
// The module checks for DOM elements before initializing
import '../dashboard/new_event_modal.js'

// Import inline edit utility (makes functions globally available)
import '../utils/inlineEdit.js'

// Import Blume-styled confirm dialog (exposes window.blumeConfirm globally)
import '../utils/blumeConfirm'

// Run Comparison (events index): one delegated, Turbo-safe listener for every
// .run-comparison-btn trigger. Safe to call on every page — it no-ops until a
// page actually renders shared/_run_comparison_modal.
import { initRunComparisonModal } from '../utils/runComparison'
initRunComparisonModal()

// Stop a turbo frame's history promotion from cancelling a nav-link click that
// landed in the ~33ms after the frame's rows repainted (issue #357). Must be
// started before any frame navigation can happen; it is inert until one does.
import { startFramePromotionGuard } from '../utils/framePromotionGuard'
startFramePromotionGuard()

// Interactive tutorial overlay. Mounted from BOTH entry points (this one and
// entrypoints/vue.ts) because the tour walks from the ERB Events index into the
// Vue event editor, and those two bundles share nothing — see
// app/javascript/tutorials/mount.ts. Inert until something starts a tour.
import { bootTutorial } from '../tutorials/mount'
bootTutorial()

// Import and register all Stimulus controllers
import { Application } from '@hotwired/stimulus'

const application = Application.start()
application.debug = false
window.Stimulus = application

// Auto-load all controllers using Vite's glob import
const controllers = import.meta.glob('../controllers/**/*_controller.js', {
  eager: true,
})

// Register each controller
Object.entries(controllers).forEach(([path, module]) => {
  // Extract controller name from path: '../controllers/calendar_view_controller.js' -> 'calendar-view'
  const name = path
    .match(/controllers\/(.+)_controller\.js$/)?.[1]
    ?.replace(/_/g, '-')

  if (name && module.default) {
    application.register(name, module.default)
  }
})

// Add navigation hover logging for debugging header styling issues
document.addEventListener('DOMContentLoaded', function() {
  // Wait a bit for the navbar to be fully rendered
  setTimeout(() => {
    const navLinks = document.querySelectorAll('.nav-link')

    navLinks.forEach((link, index) => {
      const linkText = link.textContent?.trim() || `Link ${index + 1}`

      link.addEventListener('mouseenter', (e) => {
        const computedStyle = window.getComputedStyle(link)
        const rect = link.getBoundingClientRect()

        console.log(`[NAV HOVER] ENTERED: ${linkText}`)
        console.log(`[NAV HOVER] Position: left=${rect.left}, width=${rect.width}, height=${rect.height}`)
        console.log(`[NAV HOVER] CSS Properties:`, {
          display: computedStyle.display,
          position: computedStyle.position,
          width: computedStyle.width,
          minWidth: computedStyle.minWidth,
          maxWidth: computedStyle.maxWidth,
          height: computedStyle.height,
          flex: computedStyle.flex,
          flexShrink: computedStyle.flexShrink,
          flexGrow: computedStyle.flexGrow,
          margin: computedStyle.margin,
          padding: computedStyle.padding,
          border: computedStyle.border,
          transform: computedStyle.transform,
          transition: computedStyle.transition,
          fontWeight: computedStyle.fontWeight,
          color: computedStyle.color,
          backgroundColor: computedStyle.backgroundColor
        })

        // Store initial state for comparison
        link._navInitialRect = rect
        link._navInitialStyle = {
          width: computedStyle.width,
          flex: computedStyle.flex,
          flexShrink: computedStyle.flexShrink,
          flexGrow: computedStyle.flexGrow,
          transform: computedStyle.transform
        }

        // Log parent container info
        const navContainer = link.closest('.nav-container nav') || link.closest('nav')
        if (navContainer) {
          const navRect = navContainer.getBoundingClientRect()
          const navComputedStyle = window.getComputedStyle(navContainer)
          console.log(`[NAV HOVER] Parent Nav Container:`, {
            position: `left=${navRect.left}, width=${navRect.width}`,
            gap: navComputedStyle.gap,
            justifyContent: navComputedStyle.justifyContent,
            display: navComputedStyle.display,
            flexDirection: navComputedStyle.flexDirection
          })

          // Store container initial state
          navContainer._navInitialRect = navRect
          navContainer._navInitialGap = navComputedStyle.gap
        }

        // Store all sibling positions for comparison
        const allNavLinks = Array.from(navLinks)
        const siblingPositions = allNavLinks.map(sibling => ({
          element: sibling,
          text: sibling.textContent?.trim() || 'Unknown',
          initialRect: sibling.getBoundingClientRect()
        }))

        console.log(`[NAV HOVER] All nav links initial positions:`, siblingPositions.map(s => ({
          text: s.text,
          left: s.initialRect.left,
          width: s.initialRect.width
        })))

        // Check for changes after a short delay to catch any CSS transitions
        setTimeout(() => {
          const newRect = link.getBoundingClientRect()
          const newComputedStyle = window.getComputedStyle(link)

          if (newRect.left !== rect.left || newRect.width !== rect.width) {
            console.warn(`[NAV HOVER] POSITION CHANGED after hover:`, {
              old: { left: rect.left, width: rect.width },
              new: { left: newRect.left, width: newRect.width },
              delta: { left: newRect.left - rect.left, width: newRect.width - rect.width }
            })
          }

          if (newComputedStyle.width !== computedStyle.width ||
              newComputedStyle.flex !== computedStyle.flex ||
              newComputedStyle.flexShrink !== computedStyle.flexShrink ||
              newComputedStyle.flexGrow !== computedStyle.flexGrow) {
            console.warn(`[NAV HOVER] CSS CHANGED after hover:`, {
              width: { old: computedStyle.width, new: newComputedStyle.width },
              flex: { old: computedStyle.flex, new: newComputedStyle.flex },
              flexShrink: { old: computedStyle.flexShrink, new: newComputedStyle.flexShrink },
              flexGrow: { old: computedStyle.flexGrow, new: newComputedStyle.flexGrow }
            })
          }

          // Check all siblings for movement
          const movedSiblings = siblingPositions.filter(sibling => {
            const newSiblingRect = sibling.element.getBoundingClientRect()
            return newSiblingRect.left !== sibling.initialRect.left
          })

          if (movedSiblings.length > 0) {
            console.warn(`[NAV HOVER] SIBLINGS MOVED during hover:`, movedSiblings.map(s => ({
              text: s.text,
              oldLeft: s.initialRect.left,
              newLeft: s.element.getBoundingClientRect().left,
              delta: s.element.getBoundingClientRect().left - s.initialRect.left
            })))
          }

          // Check container changes
          const newNavContainer = link.closest('.nav-container nav') || link.closest('nav')
          if (newNavContainer) {
            const newNavRect = newNavContainer.getBoundingClientRect()
            const newNavComputedStyle = window.getComputedStyle(newNavContainer)

            if (newNavRect.width !== navContainer._navInitialRect.width ||
                newNavComputedStyle.gap !== navContainer._navInitialGap) {
              console.warn(`[NAV HOVER] CONTAINER CHANGED after hover:`, {
                width: { old: navContainer._navInitialRect.width, new: newNavRect.width },
                gap: { old: navContainer._navInitialGap, new: newNavComputedStyle.gap }
              })
            }
          }
        }, 100) // Check after 100ms for any delayed changes
      })

      link.addEventListener('mouseleave', (e) => {
        const rect = link.getBoundingClientRect()
        console.log(`[NAV HOVER] LEFT: ${linkText} - Final position: left=${rect.left}, width=${rect.width}`)
      })
    })

    console.log(`[NAV HOVER] Initialized logging for ${navLinks.length} navigation links`)
  }, 1000) // Wait 1 second for DOM to be fully ready
})
